import React, { useEffect, useMemo, useState } from 'react';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';
import Sidebar from '../../components/ui/Sidebar';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { useUserData } from '../../hooks/useUserData';
import { formatCurrencyINR } from '../../utils/currency';
import { buildApiUrl } from '../../config/api';
import { useAuth } from '../../contexts/AuthContext';
import { useDemo } from '../../contexts/DemoContext';

const EmergencyFundPage = () => {
  const { userData, saveEmergencyFund, loading } = useUserData();
  const [enabled, setEnabled] = useState(
    userData?.emergencyFund?.enabled !== undefined ? Boolean(userData.emergencyFund.enabled) : true
  );
  const [mode, setMode] = useState(userData?.emergencyFund?.mode || 'mult_expenses');
  const [flatAmount, setFlatAmount] = useState(userData?.emergencyFund?.flatAmount ?? 0);
  const [inflationPct, setInflationPct] = useState(userData?.emergencyFund?.inflationPct ?? 3.5);
  const [expenseMultiplier, setExpenseMultiplier] = useState(userData?.emergencyFund?.expenseMultiplier ?? 6);

  useEffect(() => {
    const incomingEnabled = userData?.emergencyFund?.enabled;
    setEnabled(incomingEnabled !== undefined ? Boolean(incomingEnabled) : true);
    setMode(userData?.emergencyFund?.mode || 'mult_expenses');
    setFlatAmount(userData?.emergencyFund?.flatAmount ?? 0);
    setInflationPct(userData?.emergencyFund?.inflationPct ?? 3.5);
    setExpenseMultiplier(userData?.emergencyFund?.expenseMultiplier ?? 6);
  }, [userData?.emergencyFund]);

  const isFlat = mode === 'flat';
  const isFlatInflation = mode === 'flat_inflation';
  const isMult = mode === 'mult_expenses';

  const startYear = useMemo(() => Number(userData?.setup?.startYear) || new Date().getFullYear(), [userData]);
  const horizon = useMemo(() => Math.max(1, Math.min(60, Number(userData?.setup?.projectionHorizon) || 20)), [userData]);
  const defaultExpenseInflation = Number(userData?.setup?.defaultExpenseInflation) || 0;

  const computeAnnualExpensesForYear = (year) => {
    const expenses = userData?.expenses || [];
    let total = 0;
    expenses.forEach(exp => {
      const s = Number(exp.startYear || startYear);
      const e = Number(exp.endYear || startYear);
      if (year < s || year > e) return;
      const isOneTime = exp?.isOneTime || String(exp?.frequency).toLowerCase() === 'one-time';
      // Exclude one-time expenses entirely from emergency fund calculations
      if (isOneTime) return;
      const mult = isOneTime ? 1 : (exp.frequency === 'monthly' ? 12 : exp.frequency === 'quarterly' ? 4 : 1);
      const base = Number(exp.amount || 0) * mult;
      const r = Number(exp.inflationRate ?? defaultExpenseInflation) / 100;
      const yrsFromStart = Math.max(0, year - s);
      total += base * Math.pow(1 + r, yrsFromStart);
    });
    return total;
  };

  const { getAuthHeaders } = useAuth();
  const { getDemoQueryParams } = useDemo();
  const [chartData, setChartData] = useState([]);

  useEffect(() => {
    const fetchProjection = async () => {
      try {
        const queryParams = getDemoQueryParams();
        const res = await fetch(buildApiUrl(`/api/user-data/emergency-fund/projection${queryParams}`), { headers: getAuthHeaders() });
        if (!res.ok) throw new Error('Failed to load EF projection');
        const json = await res.json();
        if (json?.success && Array.isArray(json.data)) {
          // backend returns numeric year; convert to string for XAxis compatibility
          setChartData(json.data.map((r) => ({ year: String(r.year), required: r.required })));
        } else {
          // Fallback: compute locally (kept for reference)
          // const years = Array.from({ length: horizon + 1 }, (_, i) => startYear + i);
          // setChartData(years.map((y, idx) => {
          //   let required = 0;
          //   if (enabled) {
          //     if (isFlat) required = Number(flatAmount) || 0;
          //     else if (isFlatInflation) required = (Number(flatAmount) || 0) * Math.pow(1 + (Number(inflationPct) / 100), idx);
          //     else if (isMult) required = computeAnnualExpensesForYear(y) * ((Number(expenseMultiplier) || 0) / 12);
          //   }
          //   return { year: String(y), required: Math.round(required) };
          // }));
          setChartData([]);
        }
      } catch (e) {
        console.error(e);
        // Fallback to FE calc kept commented above
        setChartData([]);
      }
    };
    fetchProjection();
  }, [enabled, mode, flatAmount, inflationPct, expenseMultiplier, startYear, horizon, userData?.expenses, getDemoQueryParams, getAuthHeaders]);

  const handleSave = async () => {
    const config = {
      enabled,
      mode,
      flatAmount: isFlat || isFlatInflation ? Number(flatAmount) || 0 : undefined,
      inflationPct: isFlatInflation ? Number(inflationPct) || 0 : undefined,
      expenseMultiplier: isMult ? Number(expenseMultiplier) || 1 : undefined
    };
    await saveEmergencyFund(config);
  };

  return (
    <div className="min-h-screen bg-background">
      <Sidebar />
      <Header />
      <main className="pt-16 pb-20 lg:pb-8 lg:ml-64">
        <div className="p-4 sm:p-6 py-6 sm:py-8">
          <Breadcrumb />

          <div className="flex items-center justify-between mb-6 sm:mb-8">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                <Icon name="Shield" size={16} className="text-primary" />
              </div>
              <div>
                <h1 className="text-lg font-semibold text-foreground">Emergency Fund</h1>
                <p className="text-sm text-muted-foreground">Configure your emergency fund policy</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button onClick={handleSave} disabled={loading}>
                <Icon name="Save" size={16} />
                Save
              </Button>
            </div>
          </div>

          <div className="bg-card rounded-lg border border-border p-6 space-y-6">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-foreground">Enable Emergency Fund</span>
              <label className="inline-flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  className="sr-only"
                  checked={enabled}
                  onChange={(e) => setEnabled(e.target.checked)}
                />
                <div
                  className={`relative inline-flex h-6 w-11 items-center rounded-full border transition-colors ${
                    enabled ? 'bg-primary border-primary' : 'bg-border border-border'
                  }`}
                >
                  <span
                    className={`inline-block h-5 w-5 transform rounded-full transition-all shadow-sm ${
                      enabled ? 'translate-x-5 bg-card' : 'translate-x-1 bg-card'
                    }`}
                  />
                </div>
                <span className="text-xs text-foreground/80">{enabled ? 'On' : 'Off'}</span>
              </label>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Mode</label>
              <div className="grid grid-cols-1 gap-3" role="radiogroup" aria-label="Emergency fund mode">
                <label className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer ${mode === 'flat' ? 'border-primary bg-primary/5' : 'border-border'} ${!enabled ? 'opacity-60 cursor-not-allowed' : ''}`}>
                  <input
                    type="radio"
                    name="ef-mode"
                    value="flat"
                    checked={mode === 'flat'}
                    onChange={(e) => setMode(e.target.value)}
                    disabled={!enabled}
                  />
                  <span className="text-sm text-foreground">Flat amount</span>
                </label>
                <label className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer ${mode === 'flat_inflation' ? 'border-primary bg-primary/5' : 'border-border'} ${!enabled ? 'opacity-60 cursor-not-allowed' : ''}`}>
                  <input
                    type="radio"
                    name="ef-mode"
                    value="flat_inflation"
                    checked={mode === 'flat_inflation'}
                    onChange={(e) => setMode(e.target.value)}
                    disabled={!enabled}
                  />
                  <span className="text-sm text-foreground">Flat amount with % inflation</span>
                </label>
                <label className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer ${mode === 'mult_expenses' ? 'border-primary bg-primary/5' : 'border-border'} ${!enabled ? 'opacity-60 cursor-not-allowed' : ''}`}>
                  <input
                    type="radio"
                    name="ef-mode"
                    value="mult_expenses"
                    checked={mode === 'mult_expenses'}
                    onChange={(e) => setMode(e.target.value)}
                    disabled={!enabled}
                  />
                  <span className="text-sm text-foreground">X months of expenses</span>
                </label>
              </div>
            </div>

            {(isFlat || isFlatInflation) && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Flat Amount (₹)</label>
                  <input
                    type="number"
                    className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground"
                    value={flatAmount}
                    onChange={(e) => setFlatAmount(e.target.value)}
                    disabled={!enabled}
                  />
                </div>
                {isFlatInflation && (
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Annual Inflation (%)</label>
                    <input
                      type="number"
                      step="0.1"
                      className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground"
                      value={inflationPct}
                      onChange={(e) => setInflationPct(e.target.value)}
                      disabled={!enabled}
                    />
                  </div>
                )}
              </div>
            )}

            {isMult && (
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Months of expenses</label>
                <input
                  type="number"
                  step="1"
                  className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground"
                  value={expenseMultiplier}
                  onChange={(e) => setExpenseMultiplier(e.target.value)}
                  disabled={!enabled}
                />
              </div>
            )}
          </div>

          <div className="bg-card rounded-lg border border-border p-6 mt-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Icon name="TrendingUp" size={16} className="text-primary" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-foreground">Emergency Fund Projection</h2>
                  <p className="text-sm text-muted-foreground">Required amount by year based on your settings</p>
                </div>
              </div>
            </div>

            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                  <XAxis dataKey="year" stroke="var(--color-muted-foreground)" fontSize={12} />
                  <YAxis tickFormatter={(v) => formatCurrencyINR(v, { compact: true })} stroke="var(--color-muted-foreground)" fontSize={12} />
                  <Tooltip content={({ active, payload, label }) => {
                    if (active && payload && payload.length) {
                      return (
                        <div className="bg-card border border-border rounded-lg p-3 shadow-floating">
                          <p className="font-medium text-foreground mb-1">Year {label}</p>
                          <p className="text-sm text-foreground">Required: {formatCurrencyINR(payload[0].value)}</p>
                        </div>
                      );
                    }
                    return null;
                  }} />
                  <Legend />
                  <Line type="monotone" dataKey="required" stroke="var(--color-primary)" strokeWidth={2} name="Required EF" dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default EmergencyFundPage;


